import React, { useState } from 'react';
import { DiscogsVerifyResult, DiscogsBatchResult } from '../../types';
import { Button } from '../shared/Button';
import './DiscogsVerify.css';

// API base URL
const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : 'http://localhost:3001/api';

// Generic song interface that works with both Song and EventSong
interface VerifiableSong {
  id: string;
  title: string;
  artist: string;
  year: number;
  imageUrl?: string;
}

interface DiscogsVerifyProps {
  songs: VerifiableSong[];
  onUpdateSong?: (songId: string, updates: { year?: number; title?: string; artist?: string; yearSource?: string }) => void;
}

interface AIYearResult {
  songId: string;
  aiYear: number;
  aiConfidence: 'high' | 'medium' | 'low';
  aiNote: string;
}

export const DiscogsVerify: React.FC<DiscogsVerifyProps> = ({ songs, onUpdateSong }) => {
  const [isConfigured, setIsConfigured] = useState<boolean | null>(null);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [verifyResult, setVerifyResult] = useState<DiscogsVerifyResult | null>(null);
  const [batchResults, setBatchResults] = useState<DiscogsBatchResult[]>([]);
  const [batchVerifying, setBatchVerifying] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [token, setToken] = useState('');
  const [configuring, setConfiguring] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiYearResults, setAiYearResults] = useState<AIYearResult[]>([]);
  const [aiVerifying, setAiVerifying] = useState(false);
  const [aiConfigured, setAiConfigured] = useState<boolean | null>(null);
  
  // Edit state per song
  const [editingSongId, setEditingSongId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{ year: string; title: string; artist: string }>({ year: '', title: '', artist: '' });

  // Check if Discogs is configured
  const checkDiscogsStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/discogs/status`);
      const data = await response.json();
      setIsConfigured(data.configured);
      if (!data.configured) {
        setShowConfig(true);
      }
    } catch (err) {
      setError('Kon Discogs status niet ophalen');
      setIsConfigured(false);
    }
  };

  // Check if AI is configured
  const checkAIStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/ai/status`);
      const data = await response.json();
      setAiConfigured(data.configured);
    } catch {
      setAiConfigured(false);
    }
  };

  // Verify years using AI
  const verifyYearsWithAI = async (songsToVerify: VerifiableSong[]) => {
    if (!aiConfigured || songsToVerify.length === 0) return;
    setAiVerifying(true);
    try {
      const response = await fetch(`${API_URL}/ai/verify-years`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ songs: songsToVerify.map(s => ({ id: s.id, title: s.title, artist: s.artist, year: s.year })) })
      });
      if (response.ok) {
        const data = await response.json();
        setAiYearResults(data.results || []);
      }
    } catch {
      // AI verify is optional, silently ignore errors
    } finally {
      setAiVerifying(false);
    }
  };

  // Get AI result for a song
  const getAIResultForSong = (songId: string): AIYearResult | undefined => {
    return aiYearResults.find(r => r.songId === songId);
  };

  // Get cross-check verdict
  const getCrossCheckVerdict = (song: VerifiableSong, batchResult: DiscogsBatchResult | undefined, aiResult: AIYearResult | undefined): { icon: string; label: string; color: string } | null => {
    const discogsYear = batchResult?.suggestedYear || batchResult?.discogsMatch?.year;
    const aiYear = aiResult?.aiYear;
    if (!discogsYear && !aiYear) return null;
    if (discogsYear && aiYear) {
      const agree = Math.abs(Number(discogsYear) - Number(aiYear)) <= 1;
      if (agree) return { icon: '🟢', label: `Discogs & AI: ${discogsYear}`, color: 'cross-agree' };
      return { icon: '🟡', label: `Discogs: ${discogsYear} / AI: ${aiYear}`, color: 'cross-disagree' };
    }
    if (discogsYear) return { icon: '💿', label: `Discogs: ${discogsYear}`, color: 'cross-discogs-only' };
    return { icon: '🤖', label: `AI: ${aiYear}`, color: 'cross-ai-only' };
  };

  // Initialize on first click
  React.useEffect(() => {
    checkDiscogsStatus();
    checkAIStatus();
  }, []);

  // Configure Discogs token
  const handleConfigure = async () => {
    if (!token.trim()) return;
    
    setConfiguring(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/discogs/configure`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: token.trim() })
      });
      
      if (response.ok) {
        setIsConfigured(true);
        setShowConfig(false);
        setToken('');
      } else {
        const data = await response.json();
        setError(data.error || 'Configuratie mislukt');
      }
    } catch (err) {
      setError('Kon verbinding niet maken met server');
    } finally {
      setConfiguring(false);
    }
  };

  // Verify a single song
  const verifySong = async (song: VerifiableSong) => {
    setVerifyingId(song.id);
    setVerifyResult(null);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/discogs/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: song.title,
          artist: song.artist,
          year: song.year
        })
      });

      if (response.ok) {
        const result: DiscogsVerifyResult = await response.json();
        setVerifyResult(result);
      } else {
        const data = await response.json();
        setError(data.error || 'Verificatie mislukt');
      }
    } catch (err) {
      setError('Kon verbinding niet maken met Discogs');
    } finally {
      setVerifyingId(null);
    }
  };

  // Verify all songs in batch
  const verifyAllSongs = async () => {
    if (songs.length === 0) return;
    
    setBatchVerifying(true);
    setBatchResults([]);
    setAiYearResults([]);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/discogs/verify-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          songs: songs.map(s => ({
            id: s.id,
            title: s.title,
            artist: s.artist,
            year: s.year
          }))
        })
      });

      if (response.ok) {
        const data = await response.json();
        setBatchResults(data.results);
        // Also run AI cross-check in parallel if configured
        verifyYearsWithAI(songs);
      } else {
        const data = await response.json();
        setError(data.error || 'Batch verificatie mislukt');
      }
    } catch (err) {
      setError('Kon verbinding niet maken met Discogs');
    } finally {
      setBatchVerifying(false);
    }
  };

  // Apply correction from Discogs to song
  const applyCorrection = (songId: string, result: DiscogsBatchResult) => {
    if (onUpdateSong && result.discogsMatch) {
      onUpdateSong(songId, {
        year: result.discogsMatch.year || undefined
      });
    }
  };

  // Start editing a song
  const startEditing = (song: VerifiableSong) => {
    setEditingSongId(song.id);
    setEditValues({
      year: song.year.toString(),
      title: song.title,
      artist: song.artist
    });
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingSongId(null);
    setEditValues({ year: '', title: '', artist: '' });
  };

  // Save edited values
  const saveEditing = (songId: string) => {
    if (onUpdateSong) {
      const updates: { year?: number; title?: string; artist?: string } = {};
      
      const yearNum = parseInt(editValues.year, 10);
      if (!isNaN(yearNum) && yearNum > 1900 && yearNum < 2100) {
        updates.year = yearNum;
      }
      if (editValues.title.trim()) {
        updates.title = editValues.title.trim();
      }
      if (editValues.artist.trim()) {
        updates.artist = editValues.artist.trim();
      }
      
      if (Object.keys(updates).length > 0) {
        onUpdateSong(songId, updates);
      }
    }
    cancelEditing();
  };

  // Apply Discogs suggestion to edit fields
  const applyDiscogsToEdit = (songId: string, result: DiscogsBatchResult) => {
    if (result.discogsMatch) {
      setEditValues(prev => ({
        ...prev,
        year: result.discogsMatch?.year?.toString() || prev.year,
        title: result.discogsMatch?.title || prev.title,
        artist: result.discogsMatch?.artist || prev.artist
      }));
    }
  };

  // Get verification status for a song
  const getBatchResultForSong = (songId: string) => {
    return batchResults.find(r => r.songId === songId);
  };

  // Render configuration form
  if (showConfig || isConfigured === false) {
    return (
      <div className="discogs-verify">
        <div className="discogs-config">
          <h3>💿 Discogs Configureren</h3>
          <p>
            Maak een Personal Access Token aan op{' '}
            <a href="https://www.discogs.com/settings/developers" target="_blank" rel="noopener noreferrer">
              discogs.com/settings/developers
            </a>
          </p>
          <div className="config-form">
            <input
              type="password"
              placeholder="Personal Access Token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              disabled={configuring}
            />
            <Button 
              onClick={handleConfigure} 
              disabled={!token.trim() || configuring}
            >
              {configuring ? 'Bezig...' : 'Opslaan'}
            </Button>
          </div>
          {error && <p className="error">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="discogs-verify">
      <div className="discogs-header">
        <h3>💿 Discogs Verificatie {aiConfigured && <span className="ai-badge" title="AI cross-check actief">🤖</span>}</h3>
        <div className="header-actions">
          <Button
            onClick={verifyAllSongs}
            disabled={batchVerifying || songs.length === 0}
            variant="secondary"
          >
            {batchVerifying ? 'Bezig...' : `Alle ${songs.length} songs controleren`}
          </Button>
          {aiVerifying && <span className="ai-verifying-badge">🤖 AI bezig...</span>}
          <button 
            className="config-btn"
            onClick={() => setShowConfig(true)}
            title="Discogs configureren"
          >
            ⚙️
          </button>
        </div>
      </div>

      {error && <p className="error">{error}</p>}

      {/* Single song verification result */}
      {verifyResult && (
        <div className={`verify-result ${verifyResult.verified ? 'verified' : 'not-verified'}`}>
          <div className="result-header">
            <span className={`status ${verifyResult.verified ? 'success' : 'warning'}`}>
              {verifyResult.verified ? '✅ Geverifieerd' : '⚠️ Niet zeker'}
            </span>
            <span className="confidence">{verifyResult.confidence}% match</span>
            <button className="close-btn" onClick={() => setVerifyResult(null)}>✕</button>
          </div>
          
          <div className="match-details">
            <div className="input-vs-match">
              <div className="input-data">
                <strong>Jouw invoer:</strong>
                <p>{verifyResult.input.artist} - {verifyResult.input.title}</p>
                <p>Jaar: {verifyResult.input.year || 'onbekend'}</p>
              </div>
              <div className="match-data">
                <strong>Discogs match:</strong>
                <p>{verifyResult.bestMatch.artist} - {verifyResult.bestMatch.title}</p>
                <p>Jaar: {verifyResult.bestMatch.year || 'onbekend'}</p>
                {verifyResult.bestMatch.label && <p>Label: {verifyResult.bestMatch.label}</p>}
                {verifyResult.bestMatch.genre && <p>Genre: {verifyResult.bestMatch.genre}</p>}
              </div>
            </div>
            
            <div className="match-scores">
              <span className={verifyResult.bestMatch.artistMatch >= 80 ? 'good' : 'bad'}>
                Artiest: {verifyResult.bestMatch.artistMatch}%
              </span>
              <span className={verifyResult.bestMatch.titleMatch >= 80 ? 'good' : 'bad'}>
                Titel: {verifyResult.bestMatch.titleMatch}%
              </span>
              <span className={verifyResult.bestMatch.yearMatch ? 'good' : verifyResult.bestMatch.yearMatch === false ? 'bad' : ''}>
                Jaar: {verifyResult.bestMatch.yearMatch === true ? '✓' : verifyResult.bestMatch.yearMatch === false ? '✗' : '?'}
              </span>
            </div>
          </div>

          {verifyResult.alternatives.length > 0 && (
            <details className="alternatives">
              <summary>Alternatieve matches ({verifyResult.alternatives.length})</summary>
              <ul>
                {verifyResult.alternatives.map((alt, i) => (
                  <li key={i}>
                    {alt.artist} - {alt.title} ({alt.year || '?'}) - {alt.confidence}% match
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* Batch results summary */}
      {batchResults.length > 0 && (
        <div className="batch-summary">
          <div className="summary-stats">
            <span className="stat verified">
              ✅ {batchResults.filter(r => r.verified).length} geverifieerd
            </span>
            <span className="stat not-verified">
              ⚠️ {batchResults.filter(r => !r.verified && !r.error).length} onzeker
            </span>
            <span className="stat errors">
              ❌ {batchResults.filter(r => r.error).length} fouten
            </span>
            <span className="stat year-correct">
              📅 {batchResults.filter(r => r.yearCorrect === true).length} jaar correct
            </span>
            <span className="stat year-wrong">
              📅 {batchResults.filter(r => r.yearCorrect === false).length} jaar fout
            </span>
          </div>
        </div>
      )}

      {/* Song list with verification status - collapsible */}
      {batchResults.length > 0 && (
        <details className="songs-verification-details" open={showDetails}>
          <summary onClick={(e) => { e.preventDefault(); setShowDetails(!showDetails); }}>
            {showDetails ? '▼' : '▶'} Bekijk details per nummer
          </summary>
          <div className="songs-verification-list">
        {songs.map(song => {
          const batchResult = getBatchResultForSong(song.id);
          const aiResult = getAIResultForSong(song.id);
          const crossCheck = getCrossCheckVerdict(song, batchResult, aiResult);
          const isEditing = editingSongId === song.id;
          
          return (
            <div key={song.id} className={`song-verify-item ${batchResult?.verified ? 'verified' : batchResult ? 'not-verified' : ''} ${isEditing ? 'editing' : ''}`}>
              {isEditing ? (
                // Edit mode
                <div className="song-edit-form">
                  <div className="edit-field">
                    <label>Artiest</label>
                    <input
                      type="text"
                      value={editValues.artist}
                      onChange={(e) => setEditValues(prev => ({ ...prev, artist: e.target.value }))}
                    />
                  </div>
                  <div className="edit-field">
                    <label>Titel</label>
                    <input
                      type="text"
                      value={editValues.title}
                      onChange={(e) => setEditValues(prev => ({ ...prev, title: e.target.value }))}
                    />
                  </div>
                  <div className="edit-field small">
                    <label>Jaar</label>
                    <input
                      type="number"
                      value={editValues.year}
                      onChange={(e) => setEditValues(prev => ({ ...prev, year: e.target.value }))}
                      min="1900"
                      max="2100"
                    />
                  </div>
                  <div className="edit-actions">
                    {batchResult?.discogsMatch && (
                      <button
                        className="apply-discogs-btn"
                        onClick={() => applyDiscogsToEdit(song.id, batchResult)}
                        title="Discogs suggestie overnemen"
                      >
                        💿 Discogs
                      </button>
                    )}
                    <button className="save-btn" onClick={() => saveEditing(song.id)}>✓ Opslaan</button>
                    <button className="cancel-btn" onClick={cancelEditing}>✕ Annuleren</button>
                  </div>
                </div>
              ) : (
                // View mode
                <>
                  <div className="song-info">
                    <span className="song-title">{song.title}</span>
                    <span className="song-artist">{song.artist}</span>
                    <span className="song-year">{song.year}</span>
                  </div>
                  
                  <div className="verification-status">
                    {batchResult && (
                      <>
                        <span className={`confidence ${batchResult.confidence >= 70 ? 'high' : batchResult.confidence >= 50 ? 'medium' : 'low'}`}>
                          {batchResult.confidence}%
                        </span>
                        {batchResult.discogsMatch?.discogsUrl && (
                          <a
                            href={batchResult.discogsMatch.discogsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="discogs-link"
                            title="Bekijk op Discogs"
                          >
                            🔗
                          </a>
                        )}
                        {batchResult.discogsMatch?.formatLabel && (
                          <span className={`format-label${batchResult.discogsMatch.formatLabel === 'Single' ? ' is-single' : ''}`}>
                            {batchResult.discogsMatch.formatLabel}
                          </span>
                        )}
                        {batchResult.yearCorrect === false && batchResult.suggestedYear && (
                          <span className="year-correction">
                            Jaar zou {batchResult.suggestedYear} zijn
                            {onUpdateSong && (
                              <button 
                                className="apply-btn"
                                onClick={() => applyCorrection(song.id, batchResult)}
                                title="Correctie toepassen"
                              >
                                ✓
                              </button>
                            )}
                          </span>
                        )}
                        {/* Show alternative years from Discogs */}
                        {batchResult.alternatives && batchResult.alternatives.length > 0 && (
                          <div className="discogs-alternatives">
                            <span className="alternatives-label">Andere jaren op Discogs:</span>
                            {batchResult.alternatives.filter(a => a.year).map((alt, i) => (
                              <span key={i} className="alt-year">
                                {alt.discogsUrl ? (
                                  <a href={alt.discogsUrl} target="_blank" rel="noopener noreferrer" title={`${alt.formatLabel || (alt.type === 'master' ? 'Master' : 'Release')} • ${alt.confidence}% match`}>
                                    {alt.year}{alt.formatLabel === 'Single' ? '♪' : ''}
                                  </a>
                                ) : (
                                  alt.year
                                )}
                                {onUpdateSong && alt.year && (
                                  <button
                                    className="apply-btn small"
                                    onClick={() => onUpdateSong(song.id, { year: Number(alt.year) })}
                                    title={`Jaar ${alt.year} overnemen`}
                                  >
                                    ✓
                                  </button>
                                )}
                              </span>
                            ))}
                          </div>
                        )}
                        {batchResult.error && (
                          <span className="error-msg">{batchResult.error}</span>
                        )}
                        {crossCheck && (
                          <span className={`cross-check ${crossCheck.color}`} title={aiResult?.aiNote || ''}>
                            {crossCheck.icon} {crossCheck.label}
                            {crossCheck.color === 'cross-disagree' && onUpdateSong && aiResult && (
                              <button
                                className="apply-btn small"
                                onClick={() => onUpdateSong(song.id, { year: aiResult.aiYear })}
                                title={`AI jaar ${aiResult.aiYear} overnemen`}
                              >
                                🤖✓
                              </button>
                            )}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  
                  <div className="song-actions">
                    {onUpdateSong && (
                      <button
                        className="edit-btn"
                        onClick={() => startEditing(song)}
                        title="Bewerken"
                      >
                        ✏️
                      </button>
                    )}
                    <Button
                      onClick={() => verifySong(song)}
                      disabled={verifyingId === song.id}
                      variant="secondary"
                      size="small"
                    >
                      {verifyingId === song.id ? '...' : '🔍'}
                    </Button>
                  </div>
                </>
              )}
            </div>
          );
        })}
          </div>
        </details>
      )}
    </div>
  );
};
